import { Meta } from "@uppy/core";
import { z } from "zod";
import { zfd } from "zod-form-data";
export interface MyMetaData extends Meta {
  id: string;
  name: string;
  user_id: string;
  table: string;
}
export const uploadSchema = z.object({
  file: zfd.file(),
});
export type UploadSchema = z.infer<typeof uploadSchema>;
export const createDbSchema = z.object({
  name: z.string(),
  project_id: z.string(),
});
export const createSqliteSchema = z.object({
  name: z.string(),
  table_id: z.string(),
  column: z.string(),
  operation: z.enum(["alter", "add", "delete"]),
  data_type: z.string(),
  constraints: z.enum(["primary_key", "required", "unique"]),
  prompt: z.string(),
});


export const modifySqliteSchema = z
  .object({ targetColumn: z.string() })
  .safeExtend(createSqliteSchema.shape);
export type SqliteSchema = z.infer<typeof createSqliteSchema>;

export const createProjectSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  passwordProtectionEnabled: z.boolean().optional().default(false),
});

export const createProjectschema = z.object({ name: z.string() });
export type PragmaTableInfoRow = {
  cid: number;
  name: string;
  type: string;
  notnull: number;
  dflt_value: string | null;
  pk: number;
};
export const SqlDataTypeSchema = z.enum([
  "integer",
  "text",
  "blob",
  "real",
  "numeric",
  "boolean",
  "date",
  "datetime",
  "varchar",
  "json",
]);

const SQLITE_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;
const SQLITE_NAME_MAX_LENGTH = 64;
const SQLITE_NAME_MESSAGE =
  "Use lowercase letters, numbers, and underscores. Start with a letter.";

const sqliteNameSchema = z
  .string()
  .min(1, "Name is required")
  .max(SQLITE_NAME_MAX_LENGTH, "Name must be 64 characters or fewer")
  .regex(SQLITE_NAME_PATTERN, SQLITE_NAME_MESSAGE);

const sqliteOptionalNameSchema = z
  .string()
  .max(SQLITE_NAME_MAX_LENGTH, "Name must be 64 characters or fewer")
  .refine(
    (value) => value === "" || SQLITE_NAME_PATTERN.test(value),
    SQLITE_NAME_MESSAGE,
  );

export const columnMetadataSchema = z.object({
  key: z.string(),
  prompt: z.string(),
  type: SqlDataTypeSchema
})
export type SqliteDataTypes = z.infer<typeof SqlDataTypeSchema>;

// Extended SQL data types including ARRAY and FOREIGN_KEY
export const ExtendedSqlDataTypeSchema = z.enum([
  "integer",
  "text",
  "blob",
  "real",
  "numeric",
  "boolean",
  "date",
  "datetime",
  "varchar",
  "array",
  "json",
  "foreign_key",
]);

export const createColumnSchema = z.object({
  name: sqliteNameSchema,
  prompt: z.string(),
  type: ExtendedSqlDataTypeSchema.default("text"),
  primaryKey: z.boolean().default(false),
  unique: z.boolean().default(false),
  autoIncrement: z.boolean().default(false),
  nullable: z.boolean().default(true),
  reanalyzeExisting: z.boolean().default(false),
  referencedTable: sqliteOptionalNameSchema.optional(),
  referencedColumn: sqliteOptionalNameSchema.optional(),
});
export const updateColumnSchema = createColumnSchema.safeExtend({
  repopulateValues: z.boolean(),
  targetKey: z.string(),
});
export type UpdateColumnSchema = z.infer<typeof updateColumnSchema>;
export type CreateColumnSchema = z.infer<typeof createColumnSchema>;
// Schema for inline array field definition
export const inlineArrayFieldSchema = z.object({
  name: sqliteNameSchema,
  type: SqlDataTypeSchema,
  prompt: z.string(),
  nullable: z.boolean().default(true),
});

// Schema for inline array column configuration
export const inlineArrayColumnSchema = z.object({
  name: sqliteNameSchema, // Column name in parent table
  prompt: z.string(), // Description for AI extraction
  fields: z.array(inlineArrayFieldSchema).min(1),
});

// Schema for child table configuration
export const childTableColumnSchema = z.object({
  childTableName: sqliteNameSchema,
  columns: z.array(createColumnSchema).min(1),
});

export const createTableSchema = z
  .object({
    name: sqliteNameSchema,
    columns: z.array(createColumnSchema).min(1).refine(
      (columns) => columns.filter(col => col.name !== "id").length >= 1,
      { message: "At least one column other than 'id' is required" }
    ),
    draftColumn: createColumnSchema.nullish(),
    // Optional: inline arrays (stored as JSON in parent table)
    inlineArrays: z.array(inlineArrayColumnSchema).optional().default([]),
    // Optional: child tables (separate tables with foreign key)
    childTables: z.array(childTableColumnSchema).optional().default([]),
    // Optional project-inbox routing configuration. Table creation itself
    // ignores these fields; the dashboard creates the linked classifier after
    // the physical table and metadata exist.
    routingEnabled: z.boolean().optional().default(false),
    routingDescription: z.string().optional().default(""),
    routingConfidenceThreshold: z.number().min(60).max(100).optional().default(70),
  })
  .superRefine((data, ctx) => {
    const validatePrimaryKeys = (
      columns: Array<z.infer<typeof createColumnSchema>>,
      pathPrefix: (string | number)[],
    ) => {
      const idColumn = columns.find((column) => column.name === "id");
      if (!idColumn) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "An id column is required.",
          path: [...pathPrefix, "columns"],
        });
      } else {
        if (!idColumn.primaryKey || !idColumn.autoIncrement) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "The id column must be the primary key and auto-incrementing.",
            path: [...pathPrefix, "columns"],
          });
        }
      }

      columns.forEach((column, index) => {
        if (column.name !== "id" && (column.primaryKey || column.autoIncrement)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Only the id column can be a primary key.",
            path: [...pathPrefix, "columns", index, "primaryKey"],
          });
        }
      });
    };

    validatePrimaryKeys(data.columns, []);
    data.childTables.forEach((childTable, index) => {
      validatePrimaryKeys(childTable.columns, ["childTables", index]);
    });
  });

export const aiSuggestTableSchemaInput = z.object({
  projectSlug: z
    .string()
    .describe("Project slug used to scope suggestions."),
  filename: z
    .string()
    .describe("Original PDF filename for display and context."),
  sampleId: z
    .string()
    .uuid()
    .describe("Short-lived R2 reference for the uploaded PDF sample."),
});

const aiColumnSchema = z.object({
  name: z.string().min(2).describe("Column name for the table schema."),
  prompt: z
    .string()
    .describe("User-facing prompt describing what to extract."),
  type: ExtendedSqlDataTypeSchema.describe(
    "Column data type in the extraction schema.",
  ).default("text"),
  primaryKey: z
    .boolean()
    .default(false)
    .describe("Whether this column is a primary key."),
  unique: z.boolean().default(false).describe("Whether values must be unique."),
  autoIncrement: z
    .boolean()
    .default(false)
    .describe("Whether values auto-increment in storage."),
  nullable: z
    .boolean()
    .default(true)
    .describe("Whether null values are allowed."),
  reanalyzeExisting: z
    .boolean()
    .default(false)
    .describe("Whether to reanalyze existing documents for this column."),
  referencedTable: z
    .string()
    .optional()
    .describe("Target table for foreign key columns."),
  referencedColumn: z
    .string()
    .optional()
    .describe("Target column for foreign key columns."),
});

const aiInlineArrayFieldSchema = z.object({
  name: z.string().min(1).describe("Field name inside the inline array."),
  type: SqlDataTypeSchema.describe(
    "Field data type for inline array values.",
  ),
  prompt: z
    .string()
    .describe("Prompt describing what to extract for the field."),
  nullable: z
    .boolean()
    .default(true)
    .describe("Whether null values are allowed in the field."),
});

const aiInlineArrayColumnSchema = z.object({
  name: z
    .string()
    .min(2)
    .describe("Inline array column name in the parent table."),
  prompt: z
    .string()
    .describe("Prompt describing what the array represents."),
  fields: z
    .array(aiInlineArrayFieldSchema)
    .min(1)
    .describe("Fields captured inside each array entry."),
});

const aiChildTableColumnSchema = z.object({
  childTableName: z
    .string()
    .min(2)
    .describe("Name of the child table storing repeated rows."),
  columns: z
    .array(aiColumnSchema)
    .min(1)
    .describe("Columns for the child table schema."),
});

export const aiSuggestTableSchemaOutput = z.object({
  suggestedName: z
    .string()
    .default("")
    .describe("Suggested table name derived from OCR content."),
  columns: z
    .array(aiColumnSchema)
    .describe("Primary table columns extracted from the document."),
  inlineArrays: z
    .array(aiInlineArrayColumnSchema)
    .optional()
    .default([])
    .describe("Inline array columns stored as JSON in the parent table."),
  childTables: z
    .array(aiChildTableColumnSchema)
    .optional()
    .default([])
    .describe("Child tables stored as separate tables."),
});

export type CreateTableSchema = z.infer<typeof createTableSchema>;
export type AiSuggestTableSchemaInput = z.infer<typeof aiSuggestTableSchemaInput>;
export type AiSuggestTableSchemaOutput = z.infer<typeof aiSuggestTableSchemaOutput>;

// Classifier schemas
export const createClassifierSchema = z.object({
  name: z.string().min(2).max(100),
  prompt: z.string().min(10),
  confidenceThreshold: z.number().min(60).max(100).default(70),
  tableId: z.string(),
  projectId: z.number(),
  enabled: z.boolean().default(true),
});

export const updateClassifierSchema = createClassifierSchema.partial().extend({
  id: z.string(),
});

export type CreateClassifierSchema = z.infer<typeof createClassifierSchema>;
export type UpdateClassifierSchema = z.infer<typeof updateClassifierSchema>;
export type InlineArrayFieldSchema = z.infer<typeof inlineArrayFieldSchema>;
export type InlineArrayColumnSchema = z.infer<typeof inlineArrayColumnSchema>;
export type ChildTableColumnSchema = z.infer<typeof childTableColumnSchema>;
export type CoreDatabase = Record<string, Record<string, unknown>>;
export type DynamicDB = CoreDatabase;


export const ColumnInfoSchema = z.object({
  cid: z.number().int().describe('The column ID from PRAGMA table_info.'),
  name: z.string().describe('The name of the column.'),
  type: z.string().describe('The declared data type of the column (e.g., "TEXT", "INTEGER", "REAL").'),
  logicalType: z.string().optional().describe('Logical application type derived from schema metadata (e.g., "datetime", "foreign_key").'),
  notnull: z.number().int().describe('1 if the column has a NOT NULL constraint, 0 otherwise.'),
  prompt: z.string().nullish().describe('An optional user-defined prompt or description for the column.'), // .optional() for `?`
  default: z.string().nullish().describe('The default value of the column, or null if none.'), // .nullable() for `| null`
  primaryKey: z.number().int().describe('1 if the column is part of the primary key, 0 otherwise.'),
});

export type ColumnInfo = z.infer<typeof ColumnInfoSchema>;
export type SQLiteColumnInfo = {
  cid: number;
  name: string;
  type: string; // e.g., "TEXT", "INTEGER", "REAL", etc.
  notnull: 0 | 1;
  dflt_value: string | null;
  pk: 0 | 1;
};
export type SQLiteTableSchema = {
  tableName: string;
  columns: SQLiteColumnInfo[];
};
